/**
 * The Plans and Action menus, in one place so both sidebars draw the same
 * links (src/components/planning/PlanningSidebar.tsx and
 * src/components/prioritization/Sidebar.tsx). The Strategy and Priorities menus
 * stay in Sidebar.tsx, which owns their feature gates.
 */
import {
  Bell,
  BookOpen,
  ClipboardCheck,
  LayoutDashboard,
  Gavel,
  Search,
  AlertTriangle,
  BarChart3,
  Box,
  ClipboardList,
  Compass,
  Crosshair,
  GitBranch,
  GitCompare,
  Home,
  Milestone,
  Rocket,
  Shield,
  ShieldCheck,
  TableProperties,
  Target,
  Users,
} from 'lucide-react';

export type AreaMenuMatch = 'exact' | 'viability' | 'launch' | 'prefix';

export interface AreaMenuItem {
  name: string;
  href: string;
  icon: typeof Home;
  match?: AreaMenuMatch;
  /** Drawn indented under the entry above it. */
  isSubItem?: boolean;
}

export interface AreaMenuSection {
  label: string;
  items: AreaMenuItem[];
}

/** Plans: preparing the next plan. */
export function plansMenu({ timePhasedEnabled }: { timePhasedEnabled: boolean }): AreaMenuSection[] {
  return [
    {
      label: 'The next plan',
      items: [
        { name: 'Overview', href: '/planning', icon: Home, match: 'exact' },
        { name: 'Plans', href: '/planning/plans?catalog=1', icon: ClipboardList, match: 'prefix' },
      ],
    },
    {
      label: 'Capabilities',
      items: [
        { name: 'Viability', href: '/planning/engine', icon: Crosshair, match: 'viability' },
        ...(timePhasedEnabled ? [{ name: 'Launch Planning', href: '/planning/launch-calendar', icon: Rocket, match: 'launch' as const }] : []),
      ],
    },
    {
      label: 'People & structure',
      items: [
        { name: 'People & Capacity', href: '/planning/people-capacity', icon: Users, match: 'prefix' },
        { name: 'Areas', href: '/planning/areas', icon: Box, match: 'prefix' },
        { name: 'Milestones', href: '/planning/milestones', icon: Milestone, match: 'prefix' },
      ],
    },
    {
      label: 'Plan operations',
      items: [
        { name: 'Governance', href: '/planning/governance', icon: Shield, match: 'prefix' },
        { name: 'Assumptions & Risks', href: '/planning/assumptions-risks', icon: AlertTriangle, match: 'prefix' },
        { name: 'Work Graph', href: '/planning/work-graph', icon: GitBranch, match: 'prefix' },
        { name: 'Plan Workbench', href: '/planning/spreadsheet', icon: TableProperties, match: 'prefix' },
      ],
    },
  ];
}

/** One of an open plan's own links; `gate` is the Plans menu link it belongs to. */
export interface PlanSectionLink extends AreaMenuItem {
  /** On the role-based menu a plan link is drawn only when this menu link is. */
  gate: string;
}

/**
 * The open plan's own links (/planning/plans/:cycleId/…), drawn under the Plans
 * menu by the desktop Plans sidebar and the phone's Plans drawer.
 */
export function planSectionLinks(cycleId: string): PlanSectionLink[] {
  const base = `/planning/plans/${cycleId}`;
  return [
    { name: 'Overview', href: base, icon: LayoutDashboard, match: 'exact', gate: '/planning/plans' },
    { name: 'Milestones', href: `${base}/milestones`, icon: Milestone, match: 'prefix', gate: '/planning/milestones' },
    { name: 'Work Graph', href: `${base}/work-graph`, icon: GitBranch, match: 'prefix', gate: '/planning/work-graph' },
    { name: 'Capacity', href: `${base}/capacity`, icon: Users, match: 'prefix', gate: '/planning/people-capacity' },
    { name: 'Governance', href: `${base}/governance`, icon: Shield, match: 'prefix', gate: '/planning/governance' },
    { name: 'Assumptions & Risks', href: `${base}/assumptions-risks`, icon: AlertTriangle, match: 'prefix', gate: '/planning/assumptions-risks' },
    { name: 'Changes', href: `${base}/changes`, icon: GitCompare, match: 'prefix', gate: '/planning/changes' },
    { name: 'Terms & Claims', href: `${base}/terms`, icon: ClipboardCheck, match: 'prefix', gate: '/planning/governance' },
  ];
}

/** Action: the plan of record in force, and living it out. */
// Evidence Quality moved to Action's Reports menu (it carries its own module gate there).
export function actionMenu(_opts: { evidenceEnabled: boolean }): AreaMenuSection[] {
  return [
    {
      label: 'The plan in force',
      items: [
        { name: 'Plan of Record', href: '/planning/plans', icon: ShieldCheck, match: 'prefix' },
        { name: 'Changes', href: '/planning/changes', icon: GitCompare, match: 'prefix' },
      ],
    },
    {
      // Drift, Analytics and Evidence Quality are reports: Action's Reports menu lists them
      // (REPORT_REGISTRY areas). Realignment and the Focus Map are work, not reports.
      label: 'Responding to change',
      items: [
        { name: 'Realignment', href: '/prioritization/monitor/realignment', icon: Compass, match: 'prefix' },
        { name: 'Focus Map', href: '/prioritization/monitor/map', icon: Target, match: 'prefix' },
      ],
    },
  ];
}

/** Above every menu in the Plans shell (the Priorities shell draws its own from Sidebar.tsx). */
export const SHARED_TOP: AreaMenuItem[] = [
  { name: 'Home', href: '/prioritization/dashboard', icon: Home, match: 'exact' },
  { name: 'Notifications', href: '/prioritization/notifications', icon: Bell, match: 'prefix' },
  { name: 'My work', href: '/prioritization/my-work', icon: ClipboardList, match: 'prefix' },
  { name: 'Search', href: '/prioritization/search', icon: Search, match: 'prefix' },
];

/** Below every menu in the Plans shell, above Settings. */
export const SHARED_BOTTOM: AreaMenuItem[] = [
  { name: 'Reports', href: '/prioritization/reports', icon: BarChart3, match: 'prefix' },
  { name: 'Decisions', href: '/prioritization/decisions', icon: Gavel, match: 'prefix' },
];

/** The wristband page: the words the record uses (src/pages/prioritization/WristbandPage.tsx). */
export const WRISTBAND_HREF = '/prioritization/wristband';

/** Help: below every menu, in every shell. Everyone may read it, so no permission gate. */
export const HELP_MENU: AreaMenuSection = {
  label: 'Help',
  items: [{ name: 'The Game', href: WRISTBAND_HREF, icon: BookOpen, match: 'prefix' }],
};
